import React, { useState } from 'react';
import classNames from 'classnames';
import moment, { Moment } from 'moment';
import ClearIcon from '@material-ui/icons/Clear';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, TextField } from '@material-ui/core';
import { Controller } from 'react-hook-form';
import './FieldTimePicker.global.scss';

interface Props {
  rootClassName?: string;
  className?: string;
  formProps: any;
  name: string;
  id: string;
  label: string;
  placeholder?: string;
  screenReaderInputMessage?: string;
  defaultValue?: any;
}

interface TimeInputProps {
  id: string;
  label: string;
  placeholder?: string;
  screenReaderInputMessage?: string;
  value: any;
  onChange: (value: Moment | null) => void;
  onBlur: () => void;
  inputRef: React.Ref<any>;
  error: boolean;
}

const TimeInput = ({ id, label, placeholder, screenReaderInputMessage, value, onChange, onBlur, inputRef, error }: TimeInputProps) => {
  const [open, setOpen] = useState(false);
  const [base, setBase] = useState<Moment>(() => moment());
  const [hour, setHour] = useState('12');
  const [minute, setMinute] = useState('00');
  const [period, setPeriod] = useState('AM');
  const hourValid = /^\d{1,2}$/.test(hour) && Number(hour) >= 1 && Number(hour) <= 12;
  const minuteValid = /^\d{1,2}$/.test(minute) && Number(minute) <= 59;
  const valid = hourValid && minuteValid;
  const current = value == null ? null : moment(value);
  const openPicker = () => {
    const initial = current?.isValid() ? current.clone() : moment();
    setBase(initial);
    setHour(initial.format('hh'));
    setMinute(initial.format('mm'));
    setPeriod(initial.hour() < 12 ? 'AM' : 'PM');
    setOpen(true);
  };
  const close = () => { setOpen(false); onBlur(); };
  const confirm = () => {
    if (!valid) return;
    // Clone the original Moment: date, timezone/offset and sub-minute precision stay intact.
    onChange(base.clone().hour(Number(hour) % 12 + (period === 'PM' ? 12 : 0)).minute(Number(minute)));
    close();
  };
  const fieldId = `${id}-time`;
  return (
    <>
      <div className="center av-time-field">
        <TextField
          id={fieldId}
          variant="outlined"
          label={label}
          placeholder={placeholder}
          value={current?.isValid() ? current.format('hh:mm A') : ''}
          error={error || (current !== null && !current.isValid())}
          inputRef={inputRef}
          onBlur={onBlur}
          onClick={openPicker}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
              event.preventDefault();
              openPicker();
            }
          }}
          InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><AccessTimeIcon aria-hidden="true" /></InputAdornment> }}
          inputProps={{ 'aria-label': screenReaderInputMessage || label, 'aria-haspopup': 'dialog', 'aria-expanded': open, 'aria-required': true }}
        />
        <IconButton size="small" aria-label={`Clear ${label.toLowerCase()}`} onClick={() => { onChange(null); onBlur(); }}>
          <ClearIcon />
        </IconButton>
      </div>
      <Dialog open={open} onClose={close} aria-labelledby={`${fieldId}-title`} aria-describedby={`${fieldId}-hint`} className="av-time-dialog" maxWidth="xs" fullWidth>
        <DialogTitle id={`${fieldId}-title`}>Select time</DialogTitle>
        <DialogContent onKeyDown={(event) => {
          if (event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); confirm(); }
        }}>
          <p id={`${fieldId}-hint`} className="av-time-hint">Enter the hour and minute, then choose AM or PM.</p>
          <div className="av-time-digits">
            <TextField id={`${fieldId}-hour`} label="Hour" variant="outlined" autoFocus value={hour}
              onChange={(event) => setHour(event.target.value)} onFocus={(event) => event.target.select()}
              error={!hourValid} inputProps={{ inputMode: 'numeric', maxLength: 2, 'aria-describedby': `${fieldId}-validation` }} />
            <span className="av-time-colon" aria-hidden="true">:</span>
            <TextField id={`${fieldId}-minute`} label="Minute" variant="outlined" value={minute}
              onChange={(event) => setMinute(event.target.value)} onFocus={(event) => event.target.select()}
              error={!minuteValid} inputProps={{ inputMode: 'numeric', maxLength: 2, 'aria-describedby': `${fieldId}-validation` }} />
          </div>
          <fieldset className="av-time-period">
            <legend>Time of day</legend>
            {['AM', 'PM'].map((option) => (
              <label key={option}>
                <input type="radio" name={`${fieldId}-period`} value={option} checked={period === option} onChange={() => setPeriod(option)} />
                <span>{option}</span>
              </label>
            ))}
          </fieldset>
          <p id={`${fieldId}-validation`} className={`av-time-hint${valid ? '' : ' av-time-error'}`} role={valid ? undefined : 'alert'}>
            {valid ? '12 AM is midnight. 12 PM is noon.' : 'Use an hour from 1 to 12 and a minute from 00 to 59.'}
          </p>
        </DialogContent>
        <DialogActions>
          <Button type="button" className="av-picker-cancel" onClick={close}>Cancel</Button>
          <Button type="button" color="primary" variant="contained" disabled={!valid} onClick={confirm}>Set time</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const Timepicker = ({ rootClassName, className, name, label, id, formProps: { formState: { errors }, control }, placeholder, screenReaderInputMessage, defaultValue }: Props) => (
  <div className={classNames(rootClassName || 'fieldRoot', className)}>
    <Controller name={name} control={control} defaultValue={defaultValue}
      render={({ field: { onChange, onBlur, value, ref } }) => (
        <TimeInput id={id} label={label} placeholder={placeholder} screenReaderInputMessage={screenReaderInputMessage}
          value={value} onChange={onChange} onBlur={onBlur} inputRef={ref} error={!!errors[id]} />
      )} />
  </div>
);

export default Timepicker;
